import { AnswerPill } from "@/components/conversations/activities/hot-takes/answer-pill";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import {
  isCustomStatementId,
  type HotTakeAnswer,
} from "@somethings-ui/core/hot-takes";
import { Check, Pencil, Shuffle, Trash2, X } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

export interface StatementRowValue {
  id: string;
  text: string;
  mentorAnswer: HotTakeAnswer | null;
  // true when the row came from the bank AND the text has not been edited.
  // Edited bank rows convert to `custom:<uuid>` on send and lose the shuffle icon.
  fromBankUnmodified: boolean;
}

interface StatementRowProps {
  index: number;
  value: StatementRowValue;
  canDelete: boolean;
  onTextChange: (text: string) => void;
  onAnswerChange: (answer: HotTakeAnswer) => void;
  onShuffle: () => void;
  onDelete: () => void;
}

const MIN_CHARS = 5;
const MAX_CHARS = 135;


export function StatementRow({
  index,
  value,
  canDelete,
  onTextChange,
  onAnswerChange,
  onShuffle,
  onDelete,
}: StatementRowProps) {
  const [isEditing, setIsEditing] = useState(value.text.length === 0);
  const [draft, setDraft] = useState(value.text);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const textButtonRef = useRef<HTMLButtonElement>(null);
  const wasEditing = useRef(false);

  useEffect(() => {
    if (isEditing) {
      const el = inputRef.current;
      if (el) {
        el.focus();
        const len = el.value.length;
        el.setSelectionRange(len, len);
      }
      wasEditing.current = true;
    } else if (wasEditing.current) {
      // Moving focus off the unmounting input lands us on the dialog root,
      // which paints a focus ring across the whole modal. Re-anchor to the
      // read-state button so focus stays predictable.
      textButtonRef.current?.focus();
      wasEditing.current = false;
    }
  }, [isEditing]);

  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el || !isEditing) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [draft, isEditing]);

  const beginEdit = useCallback(() => {
    setDraft(value.text);
    setIsEditing(true);
  }, [value.text]);

  const confirmEdit = useCallback(() => {
    const trimmed = draft.trim();
    if (trimmed.length < MIN_CHARS) return;
    onTextChange(trimmed);
    setIsEditing(false);
  }, [draft, onTextChange]);

  const cancelEdit = useCallback(() => {
    if (value.text.length === 0) {
      onDelete();
    }
    setDraft(value.text);
    setIsEditing(false);
  }, [value.text, onDelete]);

  const draftValid =
    draft.trim().length >= MIN_CHARS && draft.trim().length <= MAX_CHARS;

  const isCustom = isCustomStatementId(value.id) || !value.fromBankUnmodified;
  const indexLabel = String(index + 1).padStart(2, "0");

  return (
    <div className="flex w-full items-center gap-3 py-1">
      <span
        className="w-6 shrink-0 font-mono text-sm font-medium text-tertiary"
        aria-hidden
      >
        {indexLabel}
      </span>

      <div className="flex-1 min-w-0">
        {isEditing ? (
          <div className="flex items-center gap-2">
            <Textarea
              ref={inputRef}
              value={draft}
              maxLength={MAX_CHARS}
              rows={1}
              onChange={(e) => setDraft(e.target.value.replace(/\n+/g, " "))}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.stopPropagation();
                  if (draftValid) confirmEdit();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  e.stopPropagation();
                  cancelEdit();
                }
              }}
              aria-label={`Statement ${index + 1} text`}
              placeholder="Write your take…"
              className="min-h-9 resize-none overflow-hidden whitespace-pre-wrap break-words py-1.5 text-base leading-snug"
            />
            <div className="flex shrink-0 items-center gap-2">
              <IconButton
                ariaLabel="Confirm edit"
                onClick={confirmEdit}
                disabled={!draftValid}
              >
                <Check className="h-3.5 w-3.5" />
              </IconButton>
              <IconButton ariaLabel="Cancel edit" onClick={cancelEdit}>
                <X className="h-3.5 w-3.5" />
              </IconButton>
            </div>
          </div>
        ) : (
          <button
            ref={textButtonRef}
            type="button"
            onClick={beginEdit}
            className="w-full whitespace-pre-wrap break-words text-left text-base leading-snug text-text-primary transition-colors focus:outline-none hover:text-brand-periwinkle focus-visible:text-brand-periwinkle"
          >
            {value.text || "Write your take…"}
          </button>
        )}
      </div>

      {!isEditing && (
        <div className="flex shrink-0 items-center gap-2">
          <AnswerPill
            label="Agree"
            selected={value.mentorAnswer === "agree"}
            onClick={() => onAnswerChange("agree")}
          />
          <AnswerPill
            label="Disagree"
            selected={value.mentorAnswer === "disagree"}
            onClick={() => onAnswerChange("disagree")}
          />
          <div className="h-5 w-px bg-zinc-200" aria-hidden />
          <IconButton ariaLabel="Edit this take" onClick={beginEdit}>
            <Pencil className="h-3.5 w-3.5" />
          </IconButton>
          <IconButton
            ariaLabel="Shuffle this take"
            onClick={onShuffle}
            disabled={isCustom}
          >
            <Shuffle className="h-3.5 w-3.5" />
          </IconButton>
          <IconButton
            ariaLabel="Delete this take"
            onClick={onDelete}
            disabled={!canDelete}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </IconButton>
        </div>
      )}
    </div>
  );
}

function IconButton({
  children,
  ariaLabel,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  ariaLabel: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "inline-flex h-6 w-6 items-center justify-center rounded-md text-zinc-500",
        "hover:bg-zinc-100 hover:text-zinc-800",
        "disabled:cursor-not-allowed disabled:opacity-40",
      )}
    >
      {children}
    </button>
  );
}
